/**
 * Terminal 82 - Notes Orchestrator
 *
 * Manages notes state, event binding, and UI updates.
 */

import { fetchNotes, submitNote, toggleVote, deleteNote, checkAuth } from './api';
import { renderNotesSection } from './render';
import { showMsg } from '../utils/toast';
import type { NoteResponse } from '../../api/notes/lib/types';

// ============================================
// State
// ============================================

const PAGE_SIZE = 20;

interface NotesState {
  date: string;
  notes: NoteResponse[];
  total: number;
  hasMore: boolean;
  winnerId: string | null;
  userVotes: Set<string>;
  userNoteId: string | null;
  authenticated: boolean;
  loading: boolean;
}

const state: NotesState = {
  date: '',
  notes: [],
  total: 0,
  hasMore: false,
  winnerId: null,
  userVotes: new Set(),
  userNoteId: null,
  authenticated: false,
  loading: false,
};

let container: HTMLElement | null = null;

/** Get today's date in UTC as YYYY-MM-DD */
function getTodayUTC(): string {
  return new Date().toISOString().split('T')[0];
}

// ============================================
// Init & Refresh
// ============================================

export async function initNotes(): Promise<void> {
  container = document.getElementById('notes-section');
  if (!container) return;

  // Check auth state
  const auth = await checkAuth();
  state.authenticated = auth.authenticated;

  // Bind container-level click listener ONCE (delegation for votes)
  container.addEventListener('click', handleClick);

  // Sync auth changes from nav bar login via BroadcastChannel
  const bc = new BroadcastChannel('t82_auth');
  bc.onmessage = (e) => {
    const data = e.data as { type: string };
    if (data.type === 'login') {
      state.authenticated = true;
      if (state.date) refreshNotes(state.date);
    } else if (data.type === 'logout') {
      state.authenticated = false;
      state.userVotes = new Set();
      state.userNoteId = null;
      render();
      bindFormEvents();
    }
  };
}

export async function refreshNotes(date: string): Promise<void> {
  if (!container) return;
  if (state.loading) return;

  state.date = date;
  state.loading = true;

  try {
    // Fetch real notes from API
    const data = await fetchNotes(date, { limit: PAGE_SIZE, offset: 0 });
    
    state.notes = data.notes;
    state.total = data.total;
    state.hasMore = data.hasMore;
    state.winnerId = data.winnerId;
    state.userVotes = new Set(data.userVotes);
    state.userNoteId = data.userNoteId;
  } catch (err) {
    console.error('[notes] Failed to fetch:', err);
    state.notes = [];
    state.total = 0;
    state.hasMore = false;
    state.winnerId = null;
    state.userVotes = new Set();
    state.userNoteId = null;
  } finally {
    state.loading = false;
  }

  render();
  bindFormEvents();
}

async function loadMoreNotes(): Promise<void> {
  if (!container || state.loading || !state.hasMore) return;

  state.loading = true;

  try {
    const data = await fetchNotes(state.date, { limit: PAGE_SIZE, offset: state.notes.length });
    state.notes = [...state.notes, ...data.notes];
    state.hasMore = data.hasMore;
    state.total = data.total;
    // Merge new votes into existing set
    for (const v of data.userVotes) state.userVotes.add(v);
  } catch (err) {
    console.error('[notes] Failed to load more:', err);
    showMsg('Failed to load more notes', 'error');
  } finally {
    state.loading = false;
  }

  render();
  bindFormEvents();
}

// ============================================
// Render
// ============================================

function render(): void {
  if (!container) return;

  const isToday = state.date === getTodayUTC();

  container.innerHTML = renderNotesSection({
    notes: state.notes,
    total: state.total,
    hasMore: state.hasMore,
    winnerId: state.winnerId,
    userVotes: state.userVotes,
    userNoteId: state.userNoteId,
    authenticated: state.authenticated,
    date: state.date,
    isToday,
  });
}

// ============================================
// Event Binding
// ============================================

/**
 * Bind events on elements that are recreated each render (form, edit, load-more).
 * The container-level click listener for votes is bound once in initNotes().
 */
function bindFormEvents(): void {
  if (!container) return;

  // Form submit
  const form = container.querySelector('#notes-form') as HTMLFormElement | null;
  form?.addEventListener('submit', handleSubmit);

  // Load more button
  const loadMoreBtn = container.querySelector('#notes-load-more');
  loadMoreBtn?.addEventListener('click', loadMoreNotes);
}

async function handleSubmit(e: Event): Promise<void> {
  e.preventDefault();
  if (!state.authenticated || !state.date) return;
  if (state.date !== getTodayUTC()) return;

  const textEl = document.getElementById('note-text') as HTMLTextAreaElement | null;
  const walletEl = document.getElementById('note-wallet') as HTMLInputElement | null;
  const twitterEl = document.getElementById('note-twitter') as HTMLInputElement | null;
  const submitBtn = document.getElementById('note-submit') as HTMLButtonElement | null;

  const text = textEl?.value.trim();
  if (!text) return;

  if (submitBtn) submitBtn.disabled = true;

  try {
    await submitNote({
      date: state.date,
      text,
      wallet: walletEl?.value.trim() || undefined,
      twitter: twitterEl?.value.trim() || undefined,
    });

    // Refresh to show updated list
    await refreshNotes(state.date);
  } catch (err) {
    console.error('[notes] Submit failed:', err);
    const msg = err instanceof Error ? err.message : 'Failed to submit note';
    showMsg(msg, 'error');
    if (submitBtn) submitBtn.disabled = false;
  }
}

function showDeleteConfirm(): Promise<boolean> {
  return new Promise((resolve) => {
    const overlay = document.createElement('div');
    overlay.className = 'notes-confirm-overlay';
    overlay.innerHTML = `
      <div class="notes-confirm-modal">
        <h3 class="notes-confirm-title">DELETE NOTE</h3>
        <p class="notes-confirm-message">This cannot be undone. Your votes will be lost.</p>
        <div class="notes-confirm-actions">
          <button class="notes-confirm-cancel">CANCEL</button>
          <button class="notes-confirm-delete">DELETE</button>
        </div>
      </div>
    `;

    function close(result: boolean) {
      overlay.remove();
      resolve(result);
    }

    overlay.querySelector('.notes-confirm-cancel')!.addEventListener('click', () => close(false));
    overlay.querySelector('.notes-confirm-delete')!.addEventListener('click', () => close(true));
    overlay.addEventListener('click', (ev) => {
      if (ev.target === overlay) close(false);
    });

    document.body.appendChild(overlay);
  });
}

async function handleClick(e: Event): Promise<void> {
  if (state.date !== getTodayUTC()) return;

  const target = e.target as HTMLElement;

  // Delete button
  const deleteBtn = target.closest('.note-delete-btn') as HTMLButtonElement | null;
  if (deleteBtn) {
    const noteId = deleteBtn.dataset.noteId;
    if (!noteId) return;

    const confirmed = await showDeleteConfirm();
    if (!confirmed) return;

    deleteBtn.disabled = true;
    try {
      await deleteNote(noteId);
      await refreshNotes(state.date);
    } catch (err) {
      console.error('[notes] Delete failed:', err);
      const msg = err instanceof Error ? err.message : 'Delete failed';
      showMsg(msg, 'error');
      deleteBtn.disabled = false;
    }
    return;
  }

  // Vote button
  const voteBtn = target.closest('.note-vote-btn') as HTMLButtonElement | null;
  if (!voteBtn || voteBtn.disabled) return;

  const noteId = voteBtn.dataset.noteId;
  if (!noteId) return;

  // Optimistic UI update
  const wasVoted = state.userVotes.has(noteId);
  const countEl = voteBtn.querySelector('.vote-count') as HTMLElement | null;
  const arrowEl = voteBtn.querySelector('.vote-arrow') as HTMLElement | null;
  const currentCount = parseInt(countEl?.textContent || '0', 10);

  if (wasVoted) {
    state.userVotes.delete(noteId);
    voteBtn.classList.remove('voted');
    if (arrowEl) arrowEl.textContent = '△';
    if (countEl) countEl.textContent = String(currentCount - 1);
  } else {
    state.userVotes.add(noteId);
    voteBtn.classList.add('voted');
    if (arrowEl) arrowEl.textContent = '▲';
    if (countEl) countEl.textContent = String(currentCount + 1);
  }

  try {
    const result = await toggleVote(noteId);
    // Sync with server truth
    if (countEl) countEl.textContent = String(result.voteCount);
    if (result.voted !== !wasVoted) {
      // Server disagreed, re-render
      await refreshNotes(state.date);
    }
  } catch (err) {
    console.error('[notes] Vote failed:', err);
    const msg = err instanceof Error ? err.message : 'Vote failed';
    showMsg(msg, 'error');
    // Revert optimistic update
    await refreshNotes(state.date);
  }
}
